import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Hammer, UsersThree, WarningCircle, ArrowSquareOut } from '@phosphor-icons/react';
import { resolveCanonicalRoute } from '../surfaces/shared/canonicalRefs';

const CATEGORY_CONFIG = {
  KNOWING: {
    label: 'Knowing',
    description: 'Evidence verification or SLA confirmation required',
    badgeClass: 'bg-blue-50 text-blue-800 border-blue-200',
    icon: BookOpen,
  },
  DOING: {
    label: 'Doing',
    description: 'Operational capacity or technical execution blocked',
    badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: Hammer,
  },
  BEING: {
    label: 'Being',
    description: 'Stakeholder alignment or commitment friction unresolved',
    badgeClass: 'bg-purple-50 text-purple-800 border-purple-200',
    icon: UsersThree,
  },
};

export default function DiagnosticBlockerBreakdown({ blockers = [], title = 'Readiness Constraints' }) {
  if (!blockers || blockers.length === 0) {
    return null;
  }

  return (
    <section className="rounded-lg border border-[var(--line)] bg-[var(--surface-card)] p-5 shadow-xs" data-testid="diagnostic-blocker-breakdown">
      <div className="flex items-center justify-between pb-3 mb-4 border-b border-[var(--line-soft,#EEF1F5)]">
        <div>
          <h3 className="font-heading text-sm font-bold tracking-tight text-[var(--ink-950)]">
            {title}
          </h3>
          <p className="text-xs text-[var(--ink-500)] mt-0.5">
            Diagnostic breakdown of authoritative execution conditions requiring resolution before stage sign-off.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {blockers.map((blocker, index) => {
          const item = typeof blocker === 'string'
            ? { id: `cond-${index}`, title: blocker, statement: blocker, category: 'DOING', status: 'REQUIRED' }
            : blocker;
          const categoryKey = String(item.category || 'DOING').toUpperCase();
          const config = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.DOING;
          const Icon = config.icon;
          const route = item.evidenceRef ? resolveCanonicalRoute(item.evidenceRef) : null;

          return (
            <article
              key={item.id || index}
              className="p-3.5 rounded-md border border-[var(--line-soft,#EEF1F5)] bg-[var(--surface-raised,#FAFAFA)] flex flex-col gap-2"
              data-testid={`blocker-card-${item.id || index}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${config.badgeClass}`}
                    data-testid={`badge-${categoryKey.toLowerCase()}`}
                  >
                    <Icon size={12} aria-hidden />
                    <span>{config.label}</span>
                  </span>
                  <h4 className="text-xs font-bold text-[var(--ink-950)]">
                    {item.title}
                  </h4>
                </div>

                {item.status && (
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">
                    {item.status}
                  </span>
                )}
              </div>

              {item.statement && item.statement !== item.title && (
                <p className="text-xs text-[var(--ink-700)] leading-relaxed">
                  {item.statement}
                </p>
              )}

              {route && (
                <div className="pt-1 flex items-center gap-1 text-[11px] text-[var(--ink-500)]">
                  <span>Evidence reference:</span>
                  <Link
                    to={route}
                    className="inline-flex items-center gap-1 font-semibold text-[var(--ink-900)] hover:underline"
                  >
                    <span>{blocker.evidenceRef}</span>
                    <ArrowSquareOut size={12} aria-hidden />
                  </Link>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
